import { useRef } from 'react';
import { Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { MIN_COLUMN_WIDTH } from '../../hooks/useColumnWidths';

interface ColumnResizeHandleProps {
  /** Current committed width of the column being resized (px). */
  width: number;
  /** Fired continuously while dragging with the tentative new width. */
  onPreview: (width: number) => void;
  /** Fired once on drag end with the final width. */
  onCommit: (width: number) => void;
}

/**
 * Excel-style drag handle on the right edge of a resizable header cell
 * (BUG-2026-0183). Uses window listeners for the drag so the pointer can leave
 * the handle mid-drag. stopPropagation keeps the drag from triggering the
 * header's sort toggle.
 */
export const ColumnResizeHandle = ({ width, onPreview, onCommit }: ColumnResizeHandleProps) => {
  const theme = useTheme();
  const dragRef = useRef<{ startX: number; startWidth: number; last: number } | null>(null);

  const handleMouseMove = (e: MouseEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const next = Math.max(MIN_COLUMN_WIDTH, drag.startWidth + (e.clientX - drag.startX));
    drag.last = next;
    onPreview(next);
  };

  const handleMouseUp = () => {
    const drag = dragRef.current;
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    dragRef.current = null;
    if (drag) onCommit(drag.last);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { startX: e.clientX, startWidth: width, last: width };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
        width: 10,
        cursor: 'col-resize',
        userSelect: 'none',
        touchAction: 'none',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 2,
        '&::after': {
          content: '""',
          width: '2px',
          height: '55%',
          alignSelf: 'center',
          backgroundColor: 'transparent',
          transition: 'background-color 0.15s ease',
        },
        '&:hover::after': {
          backgroundColor: alpha(theme.palette.primary.main, 0.5),
        },
      }}
    />
  );
};
